import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Shield, ShieldAlert, ShieldCheck, Globe, Server, ExternalLink, Loader2, Bug, AlertTriangle } from 'lucide-react';
import { backendURL } from '../config';

// Global in-memory cache for IP reputation during the session
const clientIpCache = new Map();

export default function AbuseIPDBPopover({ ip, children, className = "", showBadge = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const hoverTimeoutRef = useRef(null);
    const popoverRef = useRef(null);

    const isInternal = !ip || 
        ip === "0.0.0.0" || 
        ip === "127.0.0.1" || 
        ip.startsWith("10.") || 
        ip.startsWith("192.168.") || 
        ip.startsWith("172.16.") || 
        ip.startsWith("172.17.") || 
        ip.startsWith("172.18.") || 
        ip.startsWith("172.19.") || 
        ip.startsWith("172.2") || 
        ip.startsWith("172.30.") || 
        ip.startsWith("172.31.");

    const fetchIntel = async () => {
        if (isInternal) {
            setData({
                ip,
                riskStatus: "INTERNAL",
                abuseConfidenceScore: 0,
                totalReports: 0,
                countryCode: "LAN",
                isp: "Private / Internal Network",
                isWhitelisted: true,
                virusTotal: {
                    malicious: 0,
                    suspicious: 0,
                    harmless: 88,
                    totalEngines: 88,
                    detectionRate: "0/88",
                    rating: "Private Network",
                    tags: ["internal-lan"],
                    link: `https://www.virustotal.com/gui/ip-address/${encodeURIComponent(ip)}`
                }
            });
            return;
        }

        if (clientIpCache.has(ip)) {
            setData(clientIpCache.get(ip));
            return;
        }

        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/mxdr/enrich?ip=${encodeURIComponent(ip)}`, { timeout: 8000 });
            if (res.data && res.data.data) {
                clientIpCache.set(ip, res.data.data);
                setData(res.data.data);
            }
        } catch (err) {
            setData({
                ip,
                riskStatus: "UNAVAILABLE",
                abuseConfidenceScore: 0,
                totalReports: 0,
                countryCode: "N/A",
                isp: "Lookup Unavailable",
                isWhitelisted: false,
                virusTotal: {
                    malicious: 0,
                    suspicious: 0,
                    harmless: 88,
                    totalEngines: 88,
                    detectionRate: "0/88",
                    rating: "Unavailable",
                    tags: [],
                    link: `https://www.virustotal.com/gui/ip-address/${encodeURIComponent(ip)}`
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleMouseEnter = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            setIsOpen(true);
            fetchIntel();
        }, 180);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        setIsOpen(false);
    };

    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        };
    }, []);

    const isMalicious = data?.riskStatus === "MALICIOUS" || (data?.abuseConfidenceScore || 0) > 25 || (data?.virusTotal?.malicious || 0) > 0;
    const isSuspicious = data?.riskStatus === "SUSPICIOUS" || ((data?.abuseConfidenceScore || 0) > 0 && (data?.abuseConfidenceScore || 0) <= 25);

    return (
        <div 
            className={`relative inline-flex items-center gap-1.5 ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span className="cursor-pointer hover:underline hover:text-blue-400 transition-colors">
                {children || ip}
            </span>

            {showBadge && data && (
                <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono font-bold ${
                    isMalicious ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                    isSuspicious ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                }`}>
                    {data.abuseConfidenceScore}%
                </span>
            )}

            {isOpen && (
                <div 
                    ref={popoverRef}
                    className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 bg-[#0a0f1d] border border-white/[0.14] rounded-2xl shadow-2xl p-4 text-left text-xs backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150"
                    style={{ pointerEvents: 'auto' }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-white/[0.08] pb-2.5 mb-3">
                        <div className="flex items-center gap-2 font-bold text-slate-200">
                            {isMalicious ? (
                                <ShieldAlert className="w-4 h-4 text-rose-400 animate-pulse" />
                            ) : isSuspicious ? (
                                <Shield className="w-4 h-4 text-amber-400" />
                            ) : (
                                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                            )}
                            <span className="text-[11px] uppercase tracking-wider text-slate-200 font-mono font-black">
                                Dual OSINT Intelligence
                            </span>
                        </div>
                        <span className="text-[9px] font-mono text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded border border-blue-500/20">
                            AbuseIPDB & VT
                        </span>
                    </div>

                    {/* Target IP */}
                    <div className="text-sm font-black font-mono text-slate-100 mb-3 flex items-center justify-between bg-black/40 px-3 py-1.5 rounded-xl border border-white/[0.04]">
                        <span>{ip}</span>
                        {loading ? (
                            <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
                        ) : (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase font-sans ${
                                isMalicious ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                isSuspicious ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            }`}>
                                {data?.riskStatus || "CLEAN"}
                            </span>
                        )}
                    </div>

                    {loading && !data ? (
                        <div className="py-6 text-center text-slate-400 text-xs font-mono flex flex-col items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                            <span>Querying AbuseIPDB & VirusTotal...</span>
                        </div>
                    ) : (
                        <div className="space-y-3 font-sans text-[11px]">
                            {/* SECTION 1: ABUSEIPDB */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                                <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-400 uppercase">
                                    <span className="flex items-center gap-1.5">
                                        <Shield className="w-3 h-3 text-sky-400" />
                                        AbuseIPDB Intel
                                    </span>
                                    <span className={`font-mono ${isMalicious ? 'text-rose-400' : 'text-emerald-400'}`}>
                                        {data?.abuseConfidenceScore ?? 0}% Confidence
                                    </span>
                                </div>

                                {/* Score Bar */}
                                <div className="w-full bg-slate-800/80 rounded-full h-1.5 overflow-hidden">
                                    <div 
                                        className={`h-full transition-all duration-300 ${
                                            isMalicious ? 'bg-rose-500' : isSuspicious ? 'bg-amber-500' : 'bg-emerald-500'
                                        }`}
                                        style={{ width: `${Math.max(5, data?.abuseConfidenceScore ?? 0)}%` }}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-2 text-[10px] pt-1">
                                    <div className="text-slate-400">
                                        Reports: <strong className="text-slate-200 font-mono">{(data?.totalReports || 0).toLocaleString()}</strong>
                                    </div>
                                    <div className="text-slate-400 text-right">
                                        Country: <strong className="text-slate-200 font-mono">{data?.countryCode || "N/A"}</strong>
                                    </div>
                                    <div className="col-span-2 text-slate-400 truncate" title={data?.isp}>
                                        ISP: <strong className="text-slate-200 font-sans">{data?.isp || "Unknown"}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* SECTION 2: VIRUSTOTAL */}
                            <div className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-2">
                                <div className="flex items-center justify-between text-[10px] font-mono font-bold text-slate-400 uppercase">
                                    <span className="flex items-center gap-1.5">
                                        <Bug className="w-3 h-3 text-purple-400" />
                                        VirusTotal Consensus
                                    </span>
                                    <span className={`font-mono font-bold ${
                                        (data?.virusTotal?.malicious || 0) > 0 ? 'text-rose-400' : 'text-emerald-400'
                                    }`}>
                                        {data?.virusTotal?.detectionRate || "0/88"}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between text-[10.5px]">
                                    <span className="text-slate-400">Vendor Verdict:</span>
                                    <span className={`font-medium ${
                                        (data?.virusTotal?.malicious || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'
                                    }`}>
                                        {data?.virusTotal?.rating || "Clean Consensus"}
                                    </span>
                                </div>

                                {/* Tags */}
                                {data?.virusTotal?.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 pt-1">
                                        {data.virusTotal.tags.map((t, idx) => (
                                            <span key={idx} className="px-1.5 py-0.2 rounded text-[9px] font-mono bg-white/[0.04] text-slate-300 border border-white/[0.06]">
                                                #{t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER EXTERNAL LINKS */}
                            <div className="pt-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] font-mono">
                                <a 
                                    href={`https://www.abuseipdb.com/check/${encodeURIComponent(ip)}`}
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="text-sky-400 hover:text-sky-300 flex items-center gap-1 transition-colors"
                                >
                                    <span>AbuseIPDB</span>
                                    <ExternalLink className="w-2.5 h-2.5" />
                                </a>

                                <a 
                                    href={data?.virusTotal?.link || `https://www.virustotal.com/gui/ip-address/${encodeURIComponent(ip)}`}
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-colors"
                                >
                                    <span>VirusTotal</span>
                                    <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Pointer */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#0a0f1d]" />
                </div>
            )}
        </div>
    );
}
